import {GearIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'

import {useAnalytics} from '@github-ui/use-analytics'
import {updateQueryParam, useWhitespacePreference} from './page-data/payloads/diff-view-settings'
import {DiffCommentsPreferenceToggle} from './components/DiffCommentsPreferenceToggle'
import {DiffLinePresentationToggles} from './components/DiffLinePresentationToggles'
import {DiffViewPreferenceToggle} from './components/DiffViewPreferenceToggle'
import {
  WhitespacePreference,
  WhitespacePreferenceFlyout,
  type WhitespacePreference as WhitespacePreferenceValue,
} from './components/WhitespacePreferenceFlyout'
import {useState} from 'react'

const ALL_DIFF_VIEW_SETTINGS = ['layout', 'comments', 'whitespace', 'line-spacing'] as const

export type AvailableDiffViewSettings = (typeof ALL_DIFF_VIEW_SETTINGS)[number]

export type DiffViewSettingsMenuContentProps = {
  additionalPreferences?: React.ReactNode
  defaultHideWhitespace?: boolean
  onUpdateWhitespace?: (newValue: boolean) => void
  onUpdateWhitespacePreference?: (preference: WhitespacePreferenceValue) => void
  reloadOnSplitPreferenceChange?: boolean
  reloadOnWhitespaceChange?: boolean
  enabledDiffViewSettings?: readonly AvailableDiffViewSettings[]
  whitespacePreference?: WhitespacePreferenceValue
}

/**
 * The menu content for diff view settings, without the ActionMenu wrapper.
 * This can be used standalone in an existing ActionList, or wrapped in DiffViewSettings.
 */
export function DiffViewSettingsMenuContent({
  additionalPreferences,
  defaultHideWhitespace = false,
  enabledDiffViewSettings = ALL_DIFF_VIEW_SETTINGS,
  onUpdateWhitespace,
  onUpdateWhitespacePreference,
  reloadOnSplitPreferenceChange = false,
  reloadOnWhitespaceChange = false,
  whitespacePreference,
}: DiffViewSettingsMenuContentProps) {
  const showLayout = enabledDiffViewSettings.includes('layout')
  const showComments = enabledDiffViewSettings.includes('comments')
  const showWhitespacePreference = enabledDiffViewSettings.includes('whitespace')
  const showDiffLinePresentationToggles = enabledDiffViewSettings.includes('line-spacing')
  const showPersistentWhitespacePreference =
    showWhitespacePreference && (whitespacePreference !== undefined || onUpdateWhitespacePreference !== undefined)
  const hideWhitespace = useWhitespacePreference(defaultHideWhitespace)
  const [localWhitespacePreference, setLocalWhitespacePreference] = useState<WhitespacePreferenceValue>(
    hideWhitespace ? WhitespacePreference.AlwaysHide : WhitespacePreference.AlwaysShow,
  )
  const selectedWhitespacePreference = whitespacePreference ?? localWhitespacePreference
  const showLegacyWhitespacePreference = showWhitespacePreference && !showPersistentWhitespacePreference
  const showPresentationToggles = showLegacyWhitespacePreference || showDiffLinePresentationToggles
  const showLayoutDivider = showLayout && (showPersistentWhitespacePreference || showComments)
  const showWhitespaceDivider = showPersistentWhitespacePreference && (showComments || showPresentationToggles)
  const showPresentationDivider =
    showPresentationToggles && (showComments || (!showPersistentWhitespacePreference && showLayout))

  const handleUpdateWhitespacePreference = (preference: WhitespacePreferenceValue) => {
    if (onUpdateWhitespacePreference) {
      onUpdateWhitespacePreference(preference)
      return
    }

    const shouldHideWhitespace = preference !== WhitespacePreference.AlwaysShow
    setLocalWhitespacePreference(preference)
    updateQueryParam('w', shouldHideWhitespace ? '1' : '0')
    onUpdateWhitespace?.(shouldHideWhitespace)

    if (reloadOnWhitespaceChange) {
      window.location.reload()
    }
  }

  return (
    <>
      {showLayout && <DiffViewPreferenceToggle reloadOnChange={reloadOnSplitPreferenceChange} />}

      {showLayoutDivider && <ActionList.Divider />}

      {showPersistentWhitespacePreference && (
        <WhitespacePreferenceFlyout
          preference={selectedWhitespacePreference}
          onSelect={handleUpdateWhitespacePreference}
        />
      )}

      {showWhitespaceDivider && <ActionList.Divider />}

      {showComments && <DiffCommentsPreferenceToggle />}

      {showPresentationDivider && <ActionList.Divider />}

      {showPresentationToggles && (
        <DiffLinePresentationToggles
          defaultHideWhitespace={defaultHideWhitespace}
          lineSpacingPreferenceAvailable={showDiffLinePresentationToggles}
          whitespacePreferenceAvailable={showLegacyWhitespacePreference}
          onUpdateWhitespace={onUpdateWhitespace}
          reloadOnChange={reloadOnWhitespaceChange ? () => window.location.reload() : undefined}
        />
      )}
      {additionalPreferences}
    </>
  )
}

export type DiffViewSettingsProps = {
  additionalPreferences?: React.ReactNode
  ariaLabel?: string
  defaultHideWhitespace?: boolean
  invisible?: boolean
  onUpdateWhitespace?: (newValue: boolean) => void
  onUpdateWhitespacePreference?: (preference: WhitespacePreferenceValue) => void
  reloadOnSplitPreferenceChange?: boolean
  reloadOnWhitespaceChange?: boolean
  whitespacePreference?: WhitespacePreferenceValue
  small?: boolean
  enabledDiffViewSettings?: readonly AvailableDiffViewSettings[]
}

export function DiffViewSettings({
  additionalPreferences,
  ariaLabel = 'Open diff view settings',
  invisible = true,
  defaultHideWhitespace,
  enabledDiffViewSettings = ALL_DIFF_VIEW_SETTINGS,
  onUpdateWhitespace,
  onUpdateWhitespacePreference,
  reloadOnSplitPreferenceChange = false,
  reloadOnWhitespaceChange = false,
  whitespacePreference,
  small,
}: DiffViewSettingsProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const handleDiffSettingsClick = () => {
    sendAnalyticsEvent('diff_view.settings.open', 'DIFF_VIEW_SETTINGS_BUTTON')
  }

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          aria-label={ariaLabel}
          icon={GearIcon}
          size={small ? 'small' : 'medium'}
          variant={invisible ? 'invisible' : 'default'}
          onClick={handleDiffSettingsClick}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <DiffViewSettingsMenuContent
            additionalPreferences={additionalPreferences}
            defaultHideWhitespace={defaultHideWhitespace}
            enabledDiffViewSettings={enabledDiffViewSettings}
            onUpdateWhitespace={onUpdateWhitespace}
            onUpdateWhitespacePreference={onUpdateWhitespacePreference}
            reloadOnSplitPreferenceChange={reloadOnSplitPreferenceChange}
            reloadOnWhitespaceChange={reloadOnWhitespaceChange}
            whitespacePreference={whitespacePreference}
          />
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
